<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import { clampSelection, selectionFromValue, valueFromSelection } from './selection'
import type { DateRangeControlProps, DateRangeSelection } from './types'
import { useDateRange } from './useDateRange'

import { parseLooseDate } from '@/shared/lib/date/parse'
import { toApiDate } from '@/shared/lib/date/toApiDate'
import { DatePicker, type DatePickerCommitIntent, type DateRangeValue } from '@/shared/ui'

const props = withDefaults(defineProps<DateRangeControlProps>(), {
  linkedIds: () => [],
  preferDefaultValue: false,
  resetKey: '',
})
const emit = defineEmits<{ change: [selection: DateRangeSelection] }>()
const range = useDateRange(props.id, { linkedIds: props.linkedIds })
const stored = range.selection.value
const model = ref<DateRangeValue>(
  stored && !props.preferDefaultValue ? valueFromSelection(stored) : props.defaultValue,
)

function asDate(value: string | null): Date | undefined {
  if (!value) return undefined
  const parsed = parseLooseDate(value)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed
}

const min = computed(() => asDate(range.bounds.value.min) ?? props.picker.min)
const max = computed(() => asDate(range.bounds.value.max) ?? props.picker.max)
const boundProps = computed(() => ({
  ...(min.value ? { min: min.value } : {}),
  ...(max.value ? { max: max.value } : {}),
}))
const datePickerProps = computed(() => ({
  ...props.picker,
  ...boundProps.value,
  showApplyAll: Boolean(props.linkedIds.length > 0 && props.picker.showApplyAll),
}))

function publish(selection: DateRangeSelection, link = false): void {
  range.setSelection(selection, link)
  model.value = valueFromSelection(selection)
  emit('change', selection)
}

function publishValue(value: DateRangeValue, link = false): void {
  const selection = selectionFromValue(value)
  if (selection) publish(selection, link)
}

function commit(intent: DatePickerCommitIntent): void {
  publishValue(intent.value, intent.scope === 'all')
}

watch(
  () => [props.picker.min, props.picker.max] as const,
  ([nextMin, nextMax]) => {
    range.setBounds({
      min: nextMin ? toApiDate(nextMin) : null,
      max: nextMax ? toApiDate(nextMax) : null,
    })
  },
  { immediate: true },
)

watch(
  () => range.bounds.value,
  (bounds) => {
    const selection = range.selection.value
    if (!selection) return
    const clamped = clampSelection(selection, bounds)
    if (clamped.startDate !== selection.startDate || clamped.endDate !== selection.endDate) {
      publish({ ...clamped, savedAt: new Date().toISOString() })
    }
  },
  { deep: true },
)

watch(
  () => props.resetKey,
  (_next, previous) => {
    if (previous !== undefined) publishValue(props.defaultValue)
  },
)

onMounted(() => {
  // Hydrating an existing choice must not refresh its age. Otherwise a stale selection can live
  // forever as long as the user visits the page once per day, defeating the 24-hour expiry.
  if (stored && !props.preferDefaultValue) {
    emit('change', stored)
    return
  }
  publishValue(model.value)
})
</script>

<template>
  <DatePicker v-model="model" v-bind="datePickerProps" @commit="commit" />
</template>
